import { z } from "zod";
import { IsoDateTime, OpaqueId } from "./common.js";
import { RecoveryPlan } from "./regulation.js";
/**
 * Moteur d'anticipation — « Aide à la décision » (renommage utilisateur
 * 2026-09-10, ex-« Retournement »). Balaie le réseau à chaque cycle moteur,
 * **projette** l'évolution des retards et des intervalles, et remonte les
 * situations qui vont dégénérer **avant** qu'elles ne le fassent, avec l'action
 * recommandée.
 *
 * **Sentinelle conseille, n'agit pas** : ces événements sont consultatifs.
 * Aucun verbe mutant, aucun acquittement en v1 — un événement disparaît de
 * lui-même quand la situation se résorbe (`since` sert juste à l'affichage).
 */
export const AnticipationKind = z.enum([
    "gap-forming", // un trou de desserte se creuse (intervalle projeté ≫ cible)
    "bunching-forming", // deux courses vont se coller (intervalle projeté ≪ cible)
    "delay-cascade", // retard qui ne se résorbe pas → relève / départ suivant menacés
]);
export const AnticipationEvent = z.object({
    /** Stable au fil des cycles (`<kind>:<lineId>:<direction>:<pivot>`) — préserve `since`. */
    id: z.string(),
    priority: z.enum(["high", "normal"]),
    kind: AnticipationKind,
    lineId: OpaqueId,
    lineCode: z.string(),
    direction: z.union([z.literal(0), z.literal(1)]),
    /** Course pivot (celle sur laquelle porte l'action), si l'événement en cible une. */
    journeyId: OpaqueId.nullable(),
    /** Arrêt où le problème se matérialise (trou / bunching), si connu. */
    atStopId: OpaqueId.nullable(),
    atStopName: z.string().nullable(),
    /** Dans combien de temps (s) le problème mord — l'horizon de projection. */
    horizonSeconds: z.number().int().nonnegative(),
    projectedHeadwaySeconds: z.number().int().nullable(),
    projectedDeviationSeconds: z.number().int().nullable(),
    /** Manœuvre suggérée : `turnback` | `continue-then-hlp` | `hold` | `early-departure`. */
    recommendedActionKind: z.string(),
    headline: z.string(), // phrase FR prête à afficher
    detail: z.string(),
    since: IsoDateTime,
    /**
     * Plan de rattrapage chiffré (OSRM + horaire sens inverse) — renseigné par
     * `GET /anticipation` (REST), **absent du delta SSE** (calcul lourd, borné).
     */
    plan: RecoveryPlan.nullable(),
});
export const AnticipationResponse = z.object({
    at: IsoDateTime,
    seq: z.number().int(),
    events: z.array(AnticipationEvent), // priorité `high` d'abord, puis plus ancien d'abord
});
//# sourceMappingURL=anticipation.js.map